/**
 * RFC 4180-style CSV parser: handles quoted fields, escaped quotes and
 * newlines inside quotes. Blank rows are dropped.
 */
export const parseCsvRows = (csvText: string): string[][] => {
  const rows: string[][] = [];
  let currentField = '';
  let currentRow: string[] = [];
  let inQuotes = false;

  for (let index = 0; index < csvText.length; index += 1) {
    const character = csvText[index];
    const nextCharacter = csvText[index + 1];

    if (inQuotes) {
      if (character === '"' && nextCharacter === '"') {
        currentField += '"';
        index += 1;
        continue;
      }
      if (character === '"') {
        inQuotes = false;
        continue;
      }
      currentField += character;
      continue;
    }

    if (character === '"') {
      inQuotes = true;
      continue;
    }
    if (character === ',') {
      currentRow.push(currentField);
      currentField = '';
      continue;
    }
    if (character === '\n') {
      currentRow.push(currentField);
      rows.push(currentRow);
      currentRow = [];
      currentField = '';
      continue;
    }
    if (character === '\r') {
      continue;
    }
    currentField += character;
  }

  if (currentField !== '' || currentRow.length > 0) {
    currentRow.push(currentField);
    rows.push(currentRow);
  }

  return rows.filter((row) => row.some((cell) => cell !== ''));
};

export type CsvTable = {
  headers: string[];
  rows: string[][];
};

/** First row is the header. Returns null when there is no data row. */
export const parseCsvTable = (csvText: string): CsvTable | null => {
  const rows = parseCsvRows(csvText);
  if (rows.length < 2 || rows[0].length === 0) {
    return null;
  }
  return { headers: rows[0], rows: rows.slice(1) };
};

/** Triggers a browser download of text content. */
export const downloadTextFile = (
  fileName: string,
  content: string,
  mimeType = 'text/csv;charset=utf-8',
): void => {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
